import {
  type ControlSettings,
  type GameAction,
  GAME_ACTIONS,
  KEYBOARD_MOVE_ACTIONS,
  loadControlSettings,
  saveControlSettings,
  createDefaultControlSettings,
  resetControlSettings,
} from './bindings';

const PITCH_LIMIT = Math.PI / 2 - 0.05;

/** Left stick axes (standard mapping). */
const AXIS_MOVE_X = 0;
const AXIS_MOVE_Y = 1;
/** Right stick axes. */
const AXIS_LOOK_X = 2;
const AXIS_LOOK_Y = 3;

export interface MovementAxes {
  /** -1..1 forward (positive = forward). */
  forward: number;
  /** -1..1 strafe (positive = right). */
  strafe: number;
}

/**
 * Central input layer: keyboard, mouse (pointer lock), Gamepad API, and touch.
 * Consumers read semantic actions / axes instead of raw DOM codes.
 */
export class InputManager {
  private settings: ControlSettings;
  private readonly keysDown = new Set<string>();
  private readonly keysPressedThisFrame = new Set<string>();
  private readonly prevKeysDown = new Set<string>();
  private readonly mouseButtonsDown = new Set<number>();
  private readonly pressedThisFrame = new Set<GameAction>();
  private readonly releasedThisFrame = new Set<GameAction>();
  private readonly actionHeld = new Map<GameAction, boolean>();
  private readonly prevGamepadButtons = new Map<number, boolean>();
  /** Virtual action holds from on-screen touch controls. */
  private readonly touchActionsHeld = new Map<GameAction, boolean>();
  /** One-frame virtual presses (e.g. pause button tap). */
  private readonly touchActionPulses = new Set<GameAction>();

  private yaw = 0;
  private pitch = 0;
  private lookDx = 0;
  private lookDy = 0;
  private touchLookDx = 0;
  private touchLookDy = 0;
  private touchMoveForward = 0;
  private touchMoveStrafe = 0;
  /** Screen-space aim for touch tap/hold (viewport client coords). */
  private touchAimX: number | null = null;
  private touchAimY: number | null = null;
  private pointerLocked = false;
  private gamepadIndex: number | null = null;
  private usingGamepad = false;
  private usingTouch = false;
  private enabled = true;
  /** When true, gameplay look/move are zeroed; menu actions still work. */
  private captureBlocked = false;
  private rebindTarget: { device: 'keyboard' | 'gamepad'; action: GameAction } | null = null;
  private rebindResolve: ((ok: boolean) => void) | null = null;
  private rebindTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly domElement: HTMLElement;
  private readonly boundKeyDown: (e: KeyboardEvent) => void;
  private readonly boundKeyUp: (e: KeyboardEvent) => void;
  private readonly boundMouseMove: (e: MouseEvent) => void;
  private readonly boundPointerDown: (e: PointerEvent) => void;
  private readonly boundPointerUp: (e: PointerEvent) => void;
  private readonly boundPointerLockChange: () => void;
  private readonly boundClick: () => void;
  private readonly boundGamepadConnected: (e: GamepadEvent) => void;
  private readonly boundGamepadDisconnected: (e: GamepadEvent) => void;
  private readonly boundContextMenu: (e: Event) => void;

  constructor(domElement: HTMLElement, initial?: ControlSettings) {
    this.domElement = domElement;
    this.settings = initial ?? loadControlSettings();

    for (const action of GAME_ACTIONS) {
      this.actionHeld.set(action, false);
    }

    this.boundKeyDown = (e) => this.onKey(e, true);
    this.boundKeyUp = (e) => this.onKey(e, false);
    this.boundMouseMove = (e) => this.onMouseMove(e);
    this.boundPointerDown = (e) => this.onPointer(e, true);
    this.boundPointerUp = (e) => this.onPointer(e, false);
    this.boundPointerLockChange = () => {
      this.pointerLocked = document.pointerLockElement === this.domElement;
    };
    this.boundClick = () => {
      if (!this.shouldRequestPointerLock()) return;
      if (!this.pointerLocked && this.enabled && !this.captureBlocked) {
        void this.domElement.requestPointerLock();
      }
    };
    this.boundGamepadConnected = (e) => {
      if (this.gamepadIndex === null) this.gamepadIndex = e.gamepad.index;
    };
    this.boundGamepadDisconnected = (e) => {
      if (this.gamepadIndex === e.gamepad.index) {
        this.gamepadIndex = null;
        this.usingGamepad = false;
        this.prevGamepadButtons.clear();
      }
    };
    this.boundContextMenu = (e) => {
      if (this.pointerLocked) e.preventDefault();
    };

    window.addEventListener('keydown', this.boundKeyDown);
    window.addEventListener('keyup', this.boundKeyUp);
    document.addEventListener('mousemove', this.boundMouseMove);
    this.domElement.addEventListener('pointerdown', this.boundPointerDown);
    window.addEventListener('pointerup', this.boundPointerUp);
    window.addEventListener('pointercancel', this.boundPointerUp);
    document.addEventListener('pointerlockchange', this.boundPointerLockChange);
    this.domElement.addEventListener('click', this.boundClick);
    this.domElement.addEventListener('contextmenu', this.boundContextMenu);
    window.addEventListener('gamepadconnected', this.boundGamepadConnected);
    window.addEventListener('gamepaddisconnected', this.boundGamepadDisconnected);

    this.discoverGamepad();
  }

  dispose(): void {
    this.cancelRebind();
    window.removeEventListener('keydown', this.boundKeyDown);
    window.removeEventListener('keyup', this.boundKeyUp);
    document.removeEventListener('mousemove', this.boundMouseMove);
    this.domElement.removeEventListener('pointerdown', this.boundPointerDown);
    window.removeEventListener('pointerup', this.boundPointerUp);
    window.removeEventListener('pointercancel', this.boundPointerUp);
    document.removeEventListener('pointerlockchange', this.boundPointerLockChange);
    this.domElement.removeEventListener('click', this.boundClick);
    this.domElement.removeEventListener('contextmenu', this.boundContextMenu);
    window.removeEventListener('gamepadconnected', this.boundGamepadConnected);
    window.removeEventListener('gamepaddisconnected', this.boundGamepadDisconnected);
    if (document.pointerLockElement === this.domElement) {
      document.exitPointerLock();
    }
  }

  getSettings(): ControlSettings {
    return this.settings;
  }

  applySettings(settings: ControlSettings): void {
    this.settings = settings;
  }

  persistSettings(): void {
    saveControlSettings(this.settings);
  }

  resetToDefaults(): ControlSettings {
    this.settings = resetControlSettings();
    return this.settings;
  }

  getYaw(): number {
    return this.yaw;
  }

  getPitch(): number {
    return this.pitch;
  }

  setLookAngles(yaw: number, pitch: number): void {
    this.yaw = yaw;
    this.pitch = clamp(pitch, -PITCH_LIMIT, PITCH_LIMIT);
  }

  isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  isUsingGamepad(): boolean {
    return this.usingGamepad;
  }

  isUsingTouch(): boolean {
    return this.usingTouch;
  }

  /** Mark that touch controls are driving gameplay (skips pointer lock). */
  setUsingTouch(active: boolean): void {
    this.usingTouch = active;
    if (active) {
      this.usingGamepad = false;
      this.exitPointerLock();
    }
  }

  setTouchMovement(forward: number, strafe: number): void {
    this.touchMoveForward = clamp(forward, -1, 1);
    this.touchMoveStrafe = clamp(strafe, -1, 1);
    if (Math.abs(forward) > 0.01 || Math.abs(strafe) > 0.01) {
      this.usingTouch = true;
      this.usingGamepad = false;
    }
  }

  /** Accumulate look deltas from a touch drag (screen pixels). */
  addTouchLook(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    this.touchLookDx += dx;
    this.touchLookDy += dy;
    this.usingTouch = true;
    this.usingGamepad = false;
  }

  /** Lock combat/mining aim to a screen point (touch tap / hold). */
  setTouchAimPoint(clientX: number, clientY: number): void {
    this.touchAimX = clientX;
    this.touchAimY = clientY;
    this.usingTouch = true;
    this.usingGamepad = false;
  }

  clearTouchAimPoint(): void {
    this.touchAimX = null;
    this.touchAimY = null;
  }

  getTouchAimPoint(): { x: number; y: number } | null {
    if (this.touchAimX === null || this.touchAimY === null) return null;
    return { x: this.touchAimX, y: this.touchAimY };
  }

  setTouchAction(action: GameAction, held: boolean): void {
    if (held) {
      this.touchActionsHeld.set(action, true);
      this.usingTouch = true;
      this.usingGamepad = false;
    } else {
      this.touchActionsHeld.delete(action);
    }
  }

  /** Fire a one-frame press edge on the next update (button taps). */
  pulseTouchAction(action: GameAction): void {
    this.touchActionPulses.add(action);
    this.usingTouch = true;
    this.usingGamepad = false;
  }

  clearTouchInput(): void {
    this.touchMoveForward = 0;
    this.touchMoveStrafe = 0;
    this.touchLookDx = 0;
    this.touchLookDy = 0;
    this.touchAimX = null;
    this.touchAimY = null;
    this.touchActionsHeld.clear();
    this.touchActionPulses.clear();
  }

  getConnectedGamepadLabel(): string | null {
    const pad = this.getActiveGamepad();
    return pad?.id ?? null;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.keysDown.clear();
      this.mouseButtonsDown.clear();
      this.clearTouchInput();
    }
  }

  setCaptureBlocked(blocked: boolean): void {
    if (this.captureBlocked === blocked) return;
    this.captureBlocked = blocked;
    if (blocked) {
      this.mouseButtonsDown.clear();
      this.lookDx = 0;
      this.lookDy = 0;
      this.clearTouchInput();
    }
  }

  requestPointerLock(): void {
    if (!this.shouldRequestPointerLock()) return;
    void this.domElement.requestPointerLock();
  }

  exitPointerLock(): void {
    if (document.pointerLockElement === this.domElement) {
      document.exitPointerLock();
    }
  }

  /**
   * Pointer lock is for mouse look on desktop. Touch / coarse-pointer devices
   * use on-screen controls; requesting lock there shows a browser overlay and
   * can block all input until reload.
   */
  private shouldRequestPointerLock(): boolean {
    if (this.usingTouch) return false;
    if (typeof window === 'undefined') return true;
    try {
      if (window.matchMedia('(pointer: coarse)').matches) return false;
      // Primary input is fine, but a touch-first device still shouldn't lock
      // (e.g. some Android tablets report fine pointer while using Chrome UI).
      if (window.matchMedia('(hover: none)').matches && (navigator.maxTouchPoints ?? 0) > 0) {
        return false;
      }
    } catch {
      // matchMedia unavailable — fall through.
    }
    return true;
  }

  /**
   * Poll gamepad, merge action state, apply look. Call once per frame.
   * @param dt seconds (for stick look rate)
   */
  update(dt: number): void {
    this.pressedThisFrame.clear();
    this.releasedThisFrame.clear();
    this.keysPressedThisFrame.clear();
    for (const code of this.keysDown) {
      if (!this.prevKeysDown.has(code)) this.keysPressedThisFrame.add(code);
    }
    this.prevKeysDown.clear();
    for (const code of this.keysDown) this.prevKeysDown.add(code);

    if (!this.enabled) {
      this.syncAllActions(false);
      this.lookDx = 0;
      this.lookDy = 0;
      return;
    }

    // Rebind capture for gamepad (keyboard handled in keydown).
    if (this.rebindTarget?.device === 'gamepad') {
      this.pollGamepadRebind();
      this.lookDx = 0;
      this.lookDy = 0;
      return;
    }

    const pad = this.getActiveGamepad();
    this.detectGamepadActivity(pad);

    // Mouse look (pointer lock).
    if (!this.captureBlocked && (this.lookDx !== 0 || this.lookDy !== 0)) {
      const sens = this.settings.mouse.sensitivity;
      const invert = this.settings.mouse.invertY ? -1 : 1;
      this.yaw -= this.lookDx * sens;
      this.pitch -= this.lookDy * sens * invert;
      this.pitch = clamp(this.pitch, -PITCH_LIMIT, PITCH_LIMIT);
      this.usingGamepad = false;
    }
    this.lookDx = 0;
    this.lookDy = 0;

    // Touch look (drag outside joystick).
    if (!this.captureBlocked && (this.touchLookDx !== 0 || this.touchLookDy !== 0)) {
      const sens = this.settings.mouse.sensitivity * 1.35;
      const invert = this.settings.mouse.invertY ? -1 : 1;
      this.yaw -= this.touchLookDx * sens;
      this.pitch -= this.touchLookDy * sens * invert;
      this.pitch = clamp(this.pitch, -PITCH_LIMIT, PITCH_LIMIT);
      this.usingTouch = true;
      this.usingGamepad = false;
    }
    this.touchLookDx = 0;
    this.touchLookDy = 0;

    // Gamepad look stick.
    if (!this.captureBlocked && pad) {
      const ldz = this.settings.gamepad.lookDeadzone;
      const lx = applyDeadzone(pad.axes[AXIS_LOOK_X] ?? 0, ldz);
      const ly = applyDeadzone(pad.axes[AXIS_LOOK_Y] ?? 0, ldz);
      if (lx !== 0 || ly !== 0) {
        const sens = this.settings.gamepad.lookSensitivity;
        const invert = this.settings.gamepad.invertY ? -1 : 1;
        this.yaw -= lx * sens * dt;
        this.pitch -= ly * sens * dt * invert;
        this.pitch = clamp(this.pitch, -PITCH_LIMIT, PITCH_LIMIT);
        this.usingGamepad = true;
      }
    }

    this.syncAllActions(true, pad);

    if (pad) {
      for (let i = 0; i < pad.buttons.length; i++) {
        this.prevGamepadButtons.set(i, pad.buttons[i]?.pressed === true);
      }
    }
  }

  wasPressed(action: GameAction): boolean {
    return this.pressedThisFrame.has(action);
  }

  /** Edge press that clears so multi-step frames only see it once. */
  consumePressed(action: GameAction): boolean {
    if (!this.pressedThisFrame.has(action)) return false;
    this.pressedThisFrame.delete(action);
    return true;
  }

  /** Edge press for a raw keyboard code (e.g. Backquote for the Dev Menu). */
  consumeKey(code: string): boolean {
    if (!this.keysPressedThisFrame.has(code)) return false;
    this.keysPressedThisFrame.delete(code);
    return true;
  }

  wasReleased(action: GameAction): boolean {
    return this.releasedThisFrame.has(action);
  }

  isHeld(action: GameAction): boolean {
    return this.actionHeld.get(action) === true;
  }

  getMovementAxes(): MovementAxes {
    if (!this.enabled || this.captureBlocked) {
      return { forward: 0, strafe: 0 };
    }

    if (Math.abs(this.touchMoveForward) > 0.01 || Math.abs(this.touchMoveStrafe) > 0.01) {
      this.usingTouch = true;
      return { forward: this.touchMoveForward, strafe: this.touchMoveStrafe };
    }

    const pad = this.getActiveGamepad();
    if (pad) {
      const dz = this.settings.gamepad.moveDeadzone;
      const lx = applyDeadzone(pad.axes[AXIS_MOVE_X] ?? 0, dz);
      const ly = applyDeadzone(pad.axes[AXIS_MOVE_Y] ?? 0, dz);
      if (Math.abs(lx) > 0.01 || Math.abs(ly) > 0.01) {
        this.usingGamepad = true;
        return { forward: -ly, strafe: lx };
      }
    }

    let forward = 0;
    let strafe = 0;
    if (this.isKeyboardActionDown('moveForward')) forward += 1;
    if (this.isKeyboardActionDown('moveBack')) forward -= 1;
    if (this.isKeyboardActionDown('moveRight')) strafe += 1;
    if (this.isKeyboardActionDown('moveLeft')) strafe -= 1;
    return { forward, strafe };
  }

  beginRebind(device: 'keyboard' | 'gamepad', action: GameAction): Promise<boolean> {
    this.cancelRebind();
    this.rebindTarget = { device, action };
    return new Promise((resolve) => {
      this.rebindResolve = resolve;
      this.rebindTimeout = setTimeout(() => {
        this.finishRebind(false);
      }, 6000);
    });
  }

  cancelRebind(): void {
    this.finishRebind(false);
  }

  isRebinding(): boolean {
    return this.rebindTarget !== null;
  }

  getRebindTarget(): { device: 'keyboard' | 'gamepad'; action: GameAction } | null {
    return this.rebindTarget;
  }

  clearKeyboardBinding(action: GameAction): void {
    this.settings.keyboard[action] = [];
    this.persistSettings();
  }

  clearGamepadBinding(action: GameAction): void {
    if (KEYBOARD_MOVE_ACTIONS.includes(action)) return;
    this.settings.gamepad.buttons[action] = [];
    this.persistSettings();
  }

  setMouseSensitivity(value: number): void {
    this.settings.mouse.sensitivity = clamp(value, 0.0004, 0.012);
    this.persistSettings();
  }

  setMouseInvertY(invert: boolean): void {
    this.settings.mouse.invertY = invert;
    this.persistSettings();
  }

  setGamepadLookSensitivity(value: number): void {
    this.settings.gamepad.lookSensitivity = clamp(value, 0.4, 8);
    this.persistSettings();
  }

  setGamepadInvertY(invert: boolean): void {
    this.settings.gamepad.invertY = invert;
    this.persistSettings();
  }

  setGamepadDeadzones(move: number, look: number): void {
    this.settings.gamepad.moveDeadzone = clamp(move, 0.05, 0.45);
    this.settings.gamepad.lookDeadzone = clamp(look, 0.05, 0.45);
    this.persistSettings();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private discoverGamepad(): void {
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (pad) {
        this.gamepadIndex = pad.index;
        break;
      }
    }
  }

  private getActiveGamepad(): Gamepad | null {
    const pads = navigator.getGamepads?.() ?? [];
    if (this.gamepadIndex !== null) {
      const pad = pads[this.gamepadIndex];
      if (pad) return pad;
    }
    for (const pad of pads) {
      if (pad) {
        this.gamepadIndex = pad.index;
        return pad;
      }
    }
    return null;
  }

  private detectGamepadActivity(pad: Gamepad | null): void {
    if (!pad) return;
    for (let i = 0; i < pad.buttons.length; i++) {
      if (pad.buttons[i]?.pressed) {
        this.usingGamepad = true;
        return;
      }
    }
    const dz = this.settings.gamepad.moveDeadzone;
    const ldz = this.settings.gamepad.lookDeadzone;
    if (
      Math.abs(applyDeadzone(pad.axes[AXIS_MOVE_X] ?? 0, dz)) > 0.01 ||
      Math.abs(applyDeadzone(pad.axes[AXIS_MOVE_Y] ?? 0, dz)) > 0.01 ||
      Math.abs(applyDeadzone(pad.axes[AXIS_LOOK_X] ?? 0, ldz)) > 0.01 ||
      Math.abs(applyDeadzone(pad.axes[AXIS_LOOK_Y] ?? 0, ldz)) > 0.01
    ) {
      this.usingGamepad = true;
    }
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (this.rebindTarget?.device === 'keyboard' && down) {
      e.preventDefault();
      if (e.code === 'Escape') {
        this.finishRebind(false);
        return;
      }
      if (e.repeat) return;
      this.assignKeyboard(this.rebindTarget.action, e.code);
      this.finishRebind(true);
      return;
    }

    if (down) this.keysDown.add(e.code);
    else this.keysDown.delete(e.code);

    if (
      down &&
      (e.code === 'Space' ||
        e.code === 'Tab' ||
        e.code === 'Backquote' ||
        e.code.startsWith('Arrow')) &&
      this.enabled &&
      !this.isTypingTarget(e.target)
    ) {
      e.preventDefault();
    }
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.pointerLocked || !this.enabled) return;
    this.lookDx += e.movementX;
    this.lookDy += e.movementY;
  }

  private onPointer(e: PointerEvent, down: boolean): void {
    if (down) this.mouseButtonsDown.add(e.button);
    else this.mouseButtonsDown.delete(e.button);
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
  }

  private isKeyboardActionDown(action: GameAction): boolean {
    for (const code of this.settings.keyboard[action]) {
      if (this.keysDown.has(code)) return true;
    }
    return false;
  }

  private isGamepadActionDown(action: GameAction, pad: Gamepad | null): boolean {
    if (!pad) return false;
    if (KEYBOARD_MOVE_ACTIONS.includes(action)) return false;
    for (const idx of this.settings.gamepad.buttons[action]) {
      if (pad.buttons[idx]?.pressed) return true;
    }
    return false;
  }

  private syncAllActions(active: boolean, pad: Gamepad | null = null): void {
    for (const action of GAME_ACTIONS) {
      let down = false;
      if (active) {
        down =
          this.isKeyboardActionDown(action) ||
          this.isGamepadActionDown(action, pad) ||
          this.touchActionsHeld.get(action) === true;
        if (this.touchActionPulses.has(action)) {
          down = true;
          this.touchActionPulses.delete(action);
        }
        if (
          action === 'primary' &&
          this.mouseButtonsDown.has(this.settings.mouse.primaryButton) &&
          this.pointerLocked &&
          !this.usingTouch
        ) {
          down = true;
        }
      }
      this.setActionDown(action, down);
    }
  }

  private setActionDown(action: GameAction, down: boolean): void {
    const was = this.actionHeld.get(action) === true;
    if (down === was) return;
    this.actionHeld.set(action, down);
    if (down) this.pressedThisFrame.add(action);
    else this.releasedThisFrame.add(action);
  }

  private pollGamepadRebind(): void {
    const pad = this.getActiveGamepad();
    if (!pad || !this.rebindTarget) return;
    for (let i = 0; i < pad.buttons.length; i++) {
      const pressed = pad.buttons[i]?.pressed === true;
      const was = this.prevGamepadButtons.get(i) === true;
      this.prevGamepadButtons.set(i, pressed);
      if (pressed && !was) {
        // B / Circle cancels
        if (i === 1) {
          this.finishRebind(false);
          return;
        }
        this.assignGamepad(this.rebindTarget.action, i);
        this.finishRebind(true);
        return;
      }
    }
  }

  private assignKeyboard(action: GameAction, code: string): void {
    for (const other of GAME_ACTIONS) {
      if (other === action) continue;
      this.settings.keyboard[other] = this.settings.keyboard[other].filter((c) => c !== code);
    }
    this.settings.keyboard[action] = [code];
    this.persistSettings();
  }

  private assignGamepad(action: GameAction, button: number): void {
    if (KEYBOARD_MOVE_ACTIONS.includes(action)) return;
    for (const other of GAME_ACTIONS) {
      if (other === action) continue;
      if (KEYBOARD_MOVE_ACTIONS.includes(other)) continue;
      this.settings.gamepad.buttons[other] = this.settings.gamepad.buttons[other].filter(
        (b) => b !== button,
      );
    }
    this.settings.gamepad.buttons[action] = [button];
    this.persistSettings();
  }

  private finishRebind(ok: boolean): void {
    if (this.rebindTimeout) {
      clearTimeout(this.rebindTimeout);
      this.rebindTimeout = null;
    }
    const resolve = this.rebindResolve;
    this.rebindResolve = null;
    this.rebindTarget = null;
    resolve?.(ok);
  }
}

function applyDeadzone(value: number, deadzone: number): number {
  const abs = Math.abs(value);
  if (abs < deadzone) return 0;
  const sign = value < 0 ? -1 : 1;
  return sign * ((abs - deadzone) / (1 - deadzone));
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export { createDefaultControlSettings, loadControlSettings };
